import { Box, Typography, Avatar, Tooltip } from '@mui/material';
import { Message as MessageIcon, Visibility as ViewIcon, Edit as EditIcon } from '@mui/icons-material';
import { GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { ColumnConfigItem, LeadSource, LEAD_SOURCE_LABELS, LEAD_SOURCE_OPTIONS } from '../../types/lead';
import { LeadSelectCell } from '../../components/leads/cells';
import { hasColumnData, DEFAULT_COLUMN_WIDTH } from './leadsUtils';
import { formatTime } from '../dateUtils';

interface RenderDefaultColumnProps {
  col: ColumnConfigItem;
  customStatuses: string[];
  handleStatusChange: (leadId: string, newStatus: string) => void;
  handleSourceChange?: (leadId: string, newSource: string) => void;
  handleView: (row: any) => void;
  handleEditLead: (row: any) => void;
  handleMessage: (conversationId: string | null) => void;
  onAddCustomLabelClick: (targetLeadId: string) => void;
  leads?: any[];
}

export const getDefaultColumnDef = ({
  col,
  customStatuses,
  handleStatusChange,
  handleSourceChange,
  handleView,
  handleEditLead,
  handleMessage,
  onAddCustomLabelClick,
  leads,
}: RenderDefaultColumnProps): GridColDef => {
  switch (col.key) {
    case 'lead':
      return {
        field: 'fullName',
        colKey: col.key,
        headerName: col.label || 'Lead',
        headerAlign: 'left',
        align: 'left',
        width: 200,
        renderCell: (params: any) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
              {params.row.firstName?.charAt(0)}
            </Avatar>
            <Box sx={{ overflow: 'hidden' }}>
              <Typography variant="body2" fontWeight="medium" noWrap title={params.value || ''} sx={{ maxWidth: '160px' }}>
                {params.value}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap title={params.row.phone ? (params.row.phone.startsWith('+') ? params.row.phone : `+${params.row.phone}`) : ''} sx={{ display: 'block', maxWidth: '160px' }}>
                {params.row.phone ? (params.row.phone.startsWith('+') ? params.row.phone : `+${params.row.phone}`) : ''}
              </Typography>
            </Box>
          </Box>
        ),
      } as any;

    case 'lead_status':
      return {
        field: 'status',
        colKey: col.key,
        headerName: col.label || 'Status',
        headerAlign: 'left',
        align: 'left',
        width: DEFAULT_COLUMN_WIDTH,
        renderCell: (params: any) => (
          <LeadSelectCell
            value={params.value}
            options={customStatuses}
            onChange={(newVal) => handleStatusChange(params.row.id, newVal)}
            onActionClick={
              onAddCustomLabelClick
                ? () => onAddCustomLabelClick(params.row.id)
                : undefined
            }
            actionText="Create Status"
            emitStringValue
            sortOptions
          />
        ),
      } as any;

    case 'source':
      return {
        field: 'source',
        colKey: col.key,
        headerName: col.label || 'Source',
        headerAlign: 'left',
        align: 'left',
        width: DEFAULT_COLUMN_WIDTH,
        renderCell: (params: any) => {
          const sourceText = LEAD_SOURCE_LABELS[params.value as LeadSource] || params.value || '';
          return handleSourceChange ? (
            <LeadSelectCell
              value={params.value}
              options={LEAD_SOURCE_OPTIONS}
              onChange={(newVal) => handleSourceChange(params.row.id, newVal)}
              emitStringValue
            />
          ) : (
            <Tooltip title={sourceText}>
              <Typography variant="body2" color="text.secondary" noWrap>
                {sourceText}
              </Typography>
            </Tooltip>
          );
        },
      } as any;

    case 'created_at':
      return {
        field: 'createdAt',
        colKey: col.key,
        headerName: col.label || 'Created',
        type: 'date',
        headerAlign: 'left',
        align: 'left',
        width: DEFAULT_COLUMN_WIDTH,
        valueGetter: (params: any) => params.row.createdAt,
        renderCell: (params: any) => (
          <Typography variant="body2" color="text.secondary">
            {formatTime(params.value)}
          </Typography>
        ),
      } as any;

    case 'notes': {
      const hasNotesData = hasColumnData(leads, 'notes');
      const EXPANDED_NOTES_WIDTH = 180;

      return {
        field: 'notes',
        colKey: col.key,
        headerName: col.label || 'Notes',
        type: 'string',
        headerAlign: 'left',
        align: 'left',
        width: hasNotesData ? EXPANDED_NOTES_WIDTH : DEFAULT_COLUMN_WIDTH,
        renderCell: (params: any) => (
          <Tooltip title={params.row.notes || ''}>
            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '100%' }}>
              {params.row.notes || '—'}
            </Typography>
          </Tooltip>
        ),
      } as any;
    }

    case 'actions':
    default:
      return {
        field: 'actions',
        colKey: col.key,
        type: 'actions',
        headerName: col.label || 'Actions',
        headerAlign: 'left',
        align: 'left',
        width: DEFAULT_COLUMN_WIDTH,
        getActions: (params: any) => [
          <GridActionsCellItem
            key="view"
            icon={<ViewIcon />}
            label="View"
            onClick={() => handleView(params.row)}
          />,
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Edit"
            onClick={() => handleEditLead(params.row)}
          />,
          <GridActionsCellItem
            key="msg"
            icon={<MessageIcon />}
            label="Message"
            onClick={() => handleMessage(params.row.conversationId)}
          />,
        ],
      } as any;
  }
};
